import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import api, { errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Track() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const send = async () => {
    if (phone.length !== 10) return toast.error("Enter valid 10-digit phone");
    setLoading(true);
    try {
      await api.post("/auth/request-otp", { phone, role: "customer" });
      toast.success("OTP sent (mock: 123456)");
      setStep(2);
    } catch (e) { toast.error(errorMessage(e)); }
    setLoading(false);
  };

  const verify = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { phone, otp, role: "customer" });
      login(data.token, data.user);
      nav("/customer/dashboard");
    } catch (e) { toast.error(errorMessage(e)); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grain">
      <Link to="/" className="absolute top-6 left-6 flex items-center gap-2 text-slate-400 hover:text-amber-400 z-10">
        <ArrowLeft className="w-4 h-4"/> Back to home
      </Link>
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 mb-6">
                <img src="/logo-mark-round.png" alt="Sakthi" className="w-10 h-10 rounded-lg bg-white p-0.5 border border-amber-500/40 object-contain"/>
                <span className="font-display text-2xl text-white">Sakthi</span>
              </div>
            <h1 className="font-display text-4xl text-white">Track your application</h1>
            <p className="text-slate-400 mt-2">Sign in with your phone number.</p>
          </div>

          <div className="card-surface p-8">
            {step === 1 && (
              <>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Phone Number</label>
                <Input data-testid="track-phone" autoFocus value={phone} maxLength={10} placeholder="10-digit phone" onChange={(e)=>setPhone(e.target.value.replace(/\D/g,""))} className="mt-2 bg-slate-900/60 border-slate-700 h-12 font-mono-alt text-lg"/>
                <Button data-testid="track-send-otp" disabled={loading} onClick={send} className="w-full mt-6 h-12 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">Send OTP <ArrowRight className="ml-2 w-4 h-4"/></Button>
              </>
            )}
            {step === 2 && (
              <div className="text-center">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">OTP sent to +91 {phone}</div>
                <div className="text-amber-500/80 text-xs mb-6">Mock mode · use 123456</div>
                <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="track-otp">
                  <InputOTPGroup>{[0,1,2,3,4,5].map(i=><InputOTPSlot key={i} index={i} className="bg-slate-900/60 border-slate-700 text-white h-14 w-12 text-xl"/>)}</InputOTPGroup>
                </InputOTP>
                <Button data-testid="track-verify" disabled={otp.length!==6 || loading} onClick={verify} className="w-full mt-6 h-12 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">Verify <ArrowRight className="ml-2 w-4 h-4"/></Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
